/**
 * RESPONSIBILITY: make a picker box three times its size, and put it back.
 *
 * THREE TIMES WHAT IT ALREADY IS, measured rather than written down. A number in the
 * stylesheet has to know the base size of every box it applies to, and there are two of
 * those — the full control and the smaller one inside a row — so the pair would drift the
 * first time either changed. Measuring once, on the first magnify, means the factor is
 * the only thing this knows.
 *
 * In place, and bounded. A thumbnail the size of a postage stamp cannot answer "is this
 * the right photograph, and is it the right way up"; a lightbox over the form loses the
 * row being edited; an unbounded zoom inside a dialog just becomes scrolling.
 */
import { useRef, useState, type CSSProperties } from 'react';

/** The most a picture is enlarged by. Three is enough to see a face. */
export const MAX_ZOOM = 3;

export interface Magnify {
  /** Put this on the box that holds the preview. */
  box: React.RefObject<HTMLLabelElement>;
  big: boolean;
  toggle: () => void;
  /** Spread onto the same box. Undefined at normal size, so the stylesheet decides. */
  style: CSSProperties | undefined;
}

export function useMagnify(): Magnify {
  const box = useRef<HTMLLabelElement>(null);
  const base = useRef<{ w: number; h: number } | null>(null);
  const [big, setBig] = useState(false);

  const toggle = () => {
    // Measured BEFORE the size changes, and only once — reading it again while magnified
    // would multiply what was already multiplied.
    const el = box.current;
    if (el && !base.current && el.offsetWidth > 0) {
      base.current = { w: el.offsetWidth, h: el.offsetHeight };
    }
    setBig(!big);
  };

  const style = big && base.current
    ? { width: base.current.w * MAX_ZOOM, height: base.current.h * MAX_ZOOM }
    : undefined;

  return { box, big, toggle, style };
}
